import { AllListCallStatistics, ListSummary } from '@/types/shared';
import CallStatistics from '@components/CallStatistics';
import { Checkbox, FormControlLabel, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { Dispatch, SetStateAction, SyntheticEvent } from 'react';
import { AllContactsSwitch } from './DetailTab';
import { useCheckRocketFormStatus } from './UnsavedRocketChangesGuard';

type PageHeadProps = {
  isLoading: boolean;
  handleTopValueChange: (newValue: string) => void;
  topTabValue: string;
  disableTabs: boolean;
  isUserACoordinator: boolean;
  isUserACaller: boolean;
  list: ListSummary | undefined;
  callStatistics: AllListCallStatistics | undefined;
  allContacts: boolean;
  handleAllContactsChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  disableToggle: boolean;
  viewContactedChecked: boolean;
  setViewContactedChecked: Dispatch<SetStateAction<boolean>>;
};

export default function PageHead({
  isLoading,
  handleTopValueChange,
  topTabValue,
  disableTabs,
  isUserACoordinator,
  isUserACaller,
  list,
  callStatistics,
  allContacts,
  handleAllContactsChange,
  disableToggle,
  viewContactedChecked,
  setViewContactedChecked,
}: PageHeadProps) {
  const { checkRocketFormStatus } = useCheckRocketFormStatus();

  const handleTabChange = async (_event: SyntheticEvent, newValue: string) => {
    if (topTabValue === 'calling') {
      try {
        const safeToNavigate = await checkRocketFormStatus();
        if (!safeToNavigate) return;
      } catch (er) {
        console.warn(er);
      }
    }

    handleTopValueChange(newValue);
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        flexWrap: "wrap",
        gap: 2,
        minWidth: 0,
        width: '100%'
      }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "1.5rem",
          marginBottom: topTabValue === 'dashboard' ? '3.5rem' : '1.5rem',
          minWidth: 0,
          flex: '1 1 240px',
          maxWidth: '100%'
        }}>
        {isLoading ? (
          <Skeleton variant="rectangular" width={'40ch'} height={`${3.75}rem`} />
        ) : (
          <Box>
            <Typography component="h2" variant="h5">{list?.name}</Typography>

            <Typography variant="subtitle1">
              {list?.campaign?.label ?? <em>No campaign set</em>}
            </Typography>
          </Box>
        )}
        {!isLoading &&
        (topTabValue === 'dashboard' ? isUserACoordinator : isUserACoordinator || isUserACaller) ? (
          <Tabs
            value={topTabValue}
            onChange={handleTabChange}
            sx={{ backgroundColor: 'white', width: 358 }}
            variant="fullWidth"
          >
            {isUserACoordinator && (
              <Tab value="dashboard" label="Dashboard" disabled={disableTabs} />
            )}
            <Tab value="detail" label="Detail" disabled={disableTabs} />
            <Tab value="calling" label="Calling" disabled={disableTabs} />
          </Tabs>
        ) : (
          <Skeleton variant="rectangular" width={358} height={'3rem'} />
        )}

        {((topTabValue !== 'dashboard' && isUserACoordinator) || topTabValue === 'calling') &&
          (!isLoading ? (
            <Box
              sx={{
                display: "flex",
                gap: "1rem"
              }}>
              {topTabValue !== 'dashboard' && isUserACoordinator && (
                <AllContactsSwitch
                  isRightSelected={allContacts}
                  onSwitchValueChanged={handleAllContactsChange}
                  isDisabled={disableToggle}
                />
              )}
              {topTabValue === 'calling' && (
                <FormControlLabel
                  value="Show completed"
                  control={
                    <Checkbox
                      checked={viewContactedChecked}
                      onChange={({ target }) => setViewContactedChecked(target.checked)}
                    />
                  }
                  label={
                    <Typography variant="button" sx={{
                      color: 'rgba(0, 0, 0, 0.54)'
                    }}>
                      Show completed
                    </Typography>
                  }
                  labelPlacement="start"
                />
              )}
            </Box>
          ) : (
            <Skeleton variant="rectangular" height="2rem" width="17rem" />
          ))}
      </Box>
      <Box sx={{ flexShrink: 0 }}>
        <CallStatistics
          callStatistics={
            topTabValue === 'dashboard' || (isUserACoordinator && allContacts)
              ? callStatistics?.list
              : callStatistics?.user
          }
          hideAnswerRate={topTabValue !== 'dashboard'}
          hideMIs={topTabValue !== 'dashboard'}
        />
      </Box>
    </Box>
  );
}
